/* PlatformProfileLayout.module.css
   Platform profile shell: hero banner, sticky bar, tab row, content area.
   Tokens only — no hex values.
*/

/* Print route: laid out at the PDF's width (1444px, as d360) with the padding
   the app's <main> gives on screen, so the hero bleeds the same way. */
.page[data-print] {
  width: 1444px;
  margin: 0 auto;
  padding: var(--space-6) var(--space-8);
  overflow: hidden;
}

.page {
  min-height: 100vh;
  /* <main> is a flex column scroller: without this the page shrinks to one
     screen, its content overflows it, and the sticky bar lets go at the fold. */
  flex-shrink: 0;
  background: var(--bg-base);
}

/* ── Not-found / error states ─────────────────────────────────────────────── */

.notFound {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.notFoundInner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-4);
  text-align: center;
}

.notFoundTitle {
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
}

.notFoundMsg {
  color: var(--text-muted);
  font-size: var(--text-md);
}

.backBtn {
  padding: var(--space-2) var(--space-4);
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: var(--text-sm);
}

.backBtn:hover {
  color: var(--text-primary);
  border-color: var(--border-strong);
}

/* ── Sticky compact header ────────────────────────────────────────────────── */

.stickyAnchor {
  position: sticky;
  top: calc(-1 * var(--space-6));
  z-index: var(--z-sticky);
  height: 0;
  margin: 0 calc(-1 * var(--space-8));
}

.stickyBar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  height: 64px;
  padding: 0 var(--space-8);
  background: color-mix(in srgb, var(--bg-base) 92%, transparent);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border-subtle);
  box-shadow: var(--shadow-md);
  opacity: 0;
  translate: 0 -8px;
  pointer-events: none;
  transition:
    opacity var(--duration-fast) var(--ease-default),
    translate var(--duration-fast) var(--ease-default);
}

.stickyBar[data-visible] {
  opacity: 1;
  translate: 0 0;
  pointer-events: auto;
}

.stickyIcon {
  border-radius: var(--radius-sm);
  flex-shrink: 0;
}

.stickyTitle {
  font-size: var(--text-xl);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.stickyMeta {
  font-size: var(--text-sm);
  color: var(--text-muted);
  white-space: nowrap;
}

.stickyControls {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-left: auto;
}

@media (max-width: 640px) {
  .stickyMeta {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .stickyBar { transition: none; }
}

/* ── Hero Banner ──────────────────────────────────────────────────────────── */

.hero {
  position: relative;
  overflow: hidden;
  /* Bleed to the edges of <main>, whose padding is 24px top / 32px sides */
  margin: calc(-1 * var(--space-6)) calc(-1 * var(--space-8)) 0;
  background: linear-gradient(160deg, var(--bg-elevated) 0%, var(--bg-base) 70%);
}

/* d360's platform banner: greyscale collage, clearly visible behind the
   title, fading into the page only at the very bottom (d360 BannerLayout:
   grayscale + ~70% black overlay). */
.heroBannerImg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  filter: grayscale(100%);
}

.heroGradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--bg-base) 70%, transparent) 0%,
    color-mix(in srgb, var(--bg-base) 62%, transparent) 20%,
    color-mix(in srgb, var(--bg-base) 66%, transparent) 72%,
    var(--bg-base) 100%
  );
}

/* Light: the greyscale collage under a light scrim reads as a flat mid-grey
   slab. Show it as a faint watermark instead, faded out towards the bottom so
   the hero melts into the page; the scrim then only needs to finish the fade. */
:global([data-theme='light']) .heroBannerImg {
  opacity: 0.16;
  mix-blend-mode: luminosity;
  mask-image: linear-gradient(to bottom, #000 35%, transparent 92%);
  -webkit-mask-image: linear-gradient(to bottom, #000 35%, transparent 92%);
}

:global([data-theme='light']) .heroGradient {
  background: linear-gradient(to bottom, transparent 60%, var(--bg-base) 100%);
}

:global([data-theme='light']) .hero {
  background: var(--bg-base);
}

.heroContent {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  padding: var(--space-6) var(--space-8) var(--space-8);
}

.heroTop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

.backButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  margin-left: calc(-1 * var(--space-2));
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  font-size: var(--text-sm);
  cursor: pointer;
}

.backButton:hover { color: var(--text-primary); }

.heroControls {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

/* Neutral (not gold): exporting doesn't change what the page shows */
.exportButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 36px;
  padding: 0 var(--space-3);
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  white-space: nowrap;
  cursor: pointer;
  transition: color var(--duration-fast), border-color var(--duration-fast), background var(--duration-fast);
}

.exportButton:hover {
  color: var(--text-primary);
  border-color: var(--border-strong);
  background: var(--input-bg-hover);
}

.exportButton:focus-visible {
  outline: 2px solid var(--accent-secondary);
  outline-offset: 2px;
}

@media (max-width: 640px) {
  .exportLabel {
    display: none;
  }
}

/* Print route: scope + export stamp where Back / Region sit on screen */
.printScope {
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
  color: var(--text-secondary);
}

.heroMain {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  min-width: 0;
}

/* Logo tile: the CDN logos are square app icons, so frame them as one */
.heroLogo {
  display: grid;
  place-items: center;
  width: 160px;
  height: 160px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-subtle);
  background: var(--bg-elevated);
  box-shadow: var(--shadow-lg);
}

/* Light: a lifted tile, not a pasted-on frame */
:global([data-theme='light']) .heroLogo {
  border-color: var(--border-default);
  box-shadow: var(--shadow-md);
}

.heroLogo > * {
  width: 100%;
  height: 100%;
  border-radius: 0;
  object-fit: cover;
}

.heroInfo {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

.heroTitleRow {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.heroName {
  margin: 0;
  font-size: var(--text-4xl, var(--text-3xl));
  font-weight: var(--weight-bold);
  line-height: 1.2;
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
}

/* Rank badge — same as the title profile's all-time revenue badge */
.heroRank {
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  padding: var(--space-1) var(--space-3);
  border: 1px solid color-mix(in srgb, var(--accent-secondary) 45%, transparent);
  border-radius: var(--radius-md);
  background: color-mix(in srgb, var(--accent-secondary) 10%, transparent);
  font-family: var(--font-display);
  font-size: 28px;
  line-height: 1;
  color: var(--text-primary);
  cursor: help;
  white-space: nowrap;
}

.heroRankPound {
  color: var(--accent-secondary-text);
}

.heroRankLabel {
  margin-left: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--text-secondary);
}

/* Facts row — thin vertical rules between items, as the title hero */
.heroFacts {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: var(--space-2);
  font-size: var(--text-md);
  color: var(--text-secondary);
}

.heroFacts > * {
  padding: 0 var(--space-4);
  border-left: 1px solid var(--border-default);
}

.heroFacts > *:first-child {
  padding-left: 0;
  border-left: 0;
}

/* ── Banner stat widgets ───────────────────────────────────────────────────── */

.widgetRow {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-4);
}

@media (max-width: 1100px) {
  .widgetRow {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .widgetRow {
    grid-template-columns: 1fr;
  }

  .heroMain {
    flex-direction: column;
    align-items: flex-start;
  }

  .heroLogo {
    width: 96px;
    height: 96px;
  }
}

/* Hero end sentinel */
.heroSentinel {
  height: 0;
}

/* ── Tabs row ─────────────────────────────────────────────────────────────── */

.tabsRow {
  position: sticky;
  /* Docks under the sticky bar (64px, pinned at -space-6) rather than behind it */
  top: calc(64px - var(--space-6));
  z-index: calc(var(--z-sticky) - 1);
  background: var(--bg-base);
  /* No border here: the tab list draws its own rule under the tabs */
  margin: 0 calc(-1 * var(--space-8));
  padding: 0 var(--space-8);
}

/* ── Tab content area ─────────────────────────────────────────────────────── */

.tabContent {
  padding-top: var(--space-6);
}

/* ── Skeleton ─────────────────────────────────────────────────────────────── */

@keyframes shimmer {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}

.skel {
  border-radius: var(--radius-md);
  background: linear-gradient(
    90deg,
    var(--bg-elevated) 25%,
    var(--bg-card) 50%,
    var(--bg-elevated) 75%
  );
  background-size: 800px 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}

.heroBannerSkeleton {
  background: var(--bg-elevated);
  margin: calc(-1 * var(--space-6)) calc(-1 * var(--space-8)) 0;
}

.heroContentSkeleton {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  padding: var(--space-6) var(--space-8);
}

.heroMainSkeleton {
  display: flex;
  align-items: flex-end;
  gap: var(--space-6);
}

.skelBack { width: 60px; height: 22px; }
.skelLogo { width: 160px; height: 160px; border-radius: var(--radius-lg); flex-shrink: 0; }
.skelTitle { width: 220px; height: 40px; }
.skelFacts { width: 400px; height: 18px; margin-top: var(--space-2); }

.heroInfoSkeleton {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.widgetRowSkeleton {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-3);
  margin-top: var(--space-4);
}

.skelWidget {
  height: 140px;
  border-radius: var(--radius-lg);
}

.skelTabBar {
  height: 44px;
  margin: var(--space-4) 0;
}

.skelChart {
  height: 300px;
  margin-top: var(--space-4);
}
